import {
  useDynamicComponentConfigParams,
  useShoppingBasketInfo,
} from '#src/clientState/providers';
import { DatameshTracking } from '#src/components/Common/DatameshTracking';
import {
  trackingDictionary,
  trackingFunctionDictionary,
  useStylePerFlow,
} from '#src/utils';
import { useTracking, useTranslation } from '@edreamsodigeo/frontend-core';
import {
  Box,
  ButtonWithLoading,
  Flex,
  Glyph,
} from '@edreamsodigeo/prisma-design-system';
import React from 'react';

type PaymentButtonProps = {
  loading: boolean;
  onCheckout: () => void;
  validate?: () => boolean;
};

export const PaymentButton = ({
  loading: propLoading,
  onCheckout,
  validate = () => false,
}: PaymentButtonProps) => {
  const { trackEvent } = useTracking();
  const { getDynamicComponentConfigPerParam } =
    useDynamicComponentConfigParams('CTA_BOX');
  const {
    loading: sbLoading = true,
    products = [],
    bundles = [],
  } = useShoppingBasketInfo() ?? {};
  const { t } = useTranslation();
  const getStyle = useStylePerFlow();

  const disabled = !products?.length && !bundles?.length;
  const isLoading = propLoading || sbLoading;

  const onClick = () => {
    if (disabled || isLoading) {
      return;
    }

    if (validate()) {
      trackEvent(trackingDictionary['onCheckoutClick']);

      const onCheckoutProductType =
        trackingFunctionDictionary['onCheckoutProductTypeClick'];
      products.forEach((product) =>
        trackEvent(onCheckoutProductType(product.type))
      );

      onCheckout();
    }
  };

  const paymentButtonContentKey = getDynamicComponentConfigPerParam({
    paramId: 'purchaseButton',
    configId: 'contentKey',
  });

  return (
    <DatameshTracking datameshId="PAYMENT_BUTTON" click load>
      <ButtonWithLoading
        id="shopping-basket-payment-button"
        data-testid="upp-payment-button"
        type="primary"
        loading={isLoading}
        disabled={disabled}
        onClick={onClick}
        sx={{
          width: '100%',
          minWidth: '100%',
        }}
      >
        <Flex
          as="span"
          alignItems="center"
          mx={getStyle({ desktop: '1x_big', mobile: null })}
          py="tiny"
        >
          <Glyph category="Security" name="Locker" size="small" />
          <Box ml="small">{t(paymentButtonContentKey)}</Box>
        </Flex>
      </ButtonWithLoading>
    </DatameshTracking>
  );
};
